import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';
import { SearchDropdown } from '../../elements/search-dropdown/search-dropdown';
import { showNotification } from '../../LanghoutBetonPortal';

import {
  DeliveryOverTime,
  PickupSubOrder,
  PickupSubOrderUpdate,
  ProdEdit,
  Product,
  ProductExact,
  ProductStockView,
  StyleObject,
} from '../../types/types';
import { repeat } from 'lit/directives/repeat.js';

import '../../elements/langhout-button/langhout-button';
import { minusIcon, plusIcon } from '../../elements/icons/icons';
import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import {
  ProductStockTypeOnRequest,
  StockStatusInStock,
  StockStatusLimited,
  StockStatusOutOfStock,
  setBodyStyleObject,
} from '../../util/util';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-update-pickup-sub-order')
export class UpdatePickupSubOrder extends LitElement {
  private readonly styleObject: StyleObject = {
    '--wrapper-width': '800px',
    '--dot-size': '30px',
  };

  static styles = [
    sharedStyles,
    css`
      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
      }

      .centered {
        justify-content: center;
      }

      .inner-wrapper {
        max-width: var(--wrapper-width);
        flex-grow: 1;
      }

      .wrapper {
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .label {
        text-align: left;
        min-width: 180px;
        font-weight: bold;
        flex-grow: 1;
        max-width: calc(var(--wrapper-width) / 3 * 2);
      }

      .prod-name {
        text-align: right;
        min-width: 180px;
        flex-grow: 1;
        max-width: calc(var(--wrapper-width) / 3);
      }

      .action-row {
        margin-bottom: 20px;
      }

      select {
        padding: 5px;
        width: calc(100% - 4px);
        height: 37.19px;
        border-radius: 5px;
        border: 2px solid black;
        font-size: 16px;
      }

      langhout-button {
        width: 200px;
      }

      .prod-row {
        position: relative;
        flex-direction: row;
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .input-column {
        margin-bottom: 25px;
        width: 190px;
        max-width: 190px;
      }

      .input-column-prod-amount {
        margin-bottom: 25px;
        width: 80px;
        max-width: 80px;
      }

      .amount-in-units {
        width: 120px;
        max-width: 120px;
      }

      .input-column-prod {
        margin-bottom: 25px;
        width: 300px;
        max-width: 300px;
      }

      .fill-block {
        width: 30px;
      }

      .product-label {
        width: 220px;
      }

      .remove-prod {
        position: absolute;
        top: 2px;
        right: 5px;
      }

      .plus-icon {
        margin-right: 5px;
      }

      .dot {
        position: absolute;
        top: 2px;
        right: calc(var(--dot-size) + 10px);
        height: var(--dot-size);
        width: var(--dot-size);
        min-width: var(--dot-size);
        border-radius: 50%;
        display: inline-block;
      }

      .dot-red {
        background-color: red;
      }

      .dot-orange {
        background-color: orange;
      }

      .dot-green {
        background-color: var(--primary-color);
      }

      .pdf-viewer {
        margin-top: 20px;
        width: 100%;
        margin-left: 20px;
        margin-right: 20px;
        max-width: var(--wrapper-width);
        min-height: 980px;
      }

      textarea {
        padding: 10px;
        width: calc(100% - 27px);
        height: 100px;
        margin-left: 5px;
      }
    `,
  ];

  @property({ type: String, attribute: true, reflect: true })
  public pickupDate: string | undefined;

  @property({ type: Object, attribute: true, reflect: true })
  public pickupSubOrder: PickupSubOrder = <PickupSubOrder>{
    products: <Product[]>[],
  };

  @property({ type: Array, attribute: true, reflect: true })
  public prods: ProdEdit[] = [];

  @query('#orderID')
  public orderID?: HTMLInputElement;

  @query('#shippingID')
  public shippingID?: HTMLInputElement;

  @query('#externalID')
  public externalID?: HTMLInputElement;

  @query('#customerName')
  public customerName?: HTMLInputElement;

  @query('#shippingCity')
  public shippingCity?: HTMLInputElement;

  @query('#packageReceiptNote')
  public packageReceiptNote?: HTMLInputElement;

  @query('#pdfNote')
  public pdfNote?: HTMLTextAreaElement;

  public prodIndex = 0;

  @property({ type: Number, attribute: true, reflect: true })
  public totalWeight = 0;

  @property({ type: String, attribute: true, reflect: true })
  private productCodeFilter = '';

  public exactProducts: ProductExact[] = [];

  private exactCalled = false;

  @property({ type: String, attribute: true, reflect: true })
  private salesOrderNumber = '';

  constructor() {
    super();
    setBodyStyleObject(this.styleObject);
  }

  protected firstUpdated(): void {
    this.searchOrderLinesExact(this.pickupSubOrder.order_id);
    this.checkStockStatus();
    this.salesOrderNumber = this.pickupSubOrder.order_id.toString();
  }

  protected updated(
    changedProperties: Map<string | number | symbol, unknown>,
  ): void {
    if (changedProperties.has('pickupDate')) {
      this.checkStockStatus();
    }
  }

  public getSubOrder = (): PickupSubOrderUpdate | undefined => {
    if (!this.pickupSubOrder) {
      return undefined;
    }

    const products = this.getProducts();
    if (products.length === 0) {
      return undefined;
    }

    const result: PickupSubOrderUpdate = {
      id: this.pickupSubOrder.id,
      order_id: this.orderID ? parseInt(this.orderID.value) : NaN,
      shipping_id: this.shippingID ? this.shippingID.value : '',
      external_id: this.externalID ? this.externalID.value : '',
      shipping_city: this.shippingCity ? this.shippingCity.value : '',
      package_receipt_note: this.packageReceiptNote
        ? this.packageReceiptNote.value
        : '',
      customer_name: this.customerName ? this.customerName.value : '',
      products: products,
      pickup_order_pdf_comment: this.pdfNote ? this.pdfNote.value : '',
    };
    const errMsg = this.validatePickupOrder(result);
    if (errMsg != '') {
      showNotification(errMsg);
      return;
    }
    return result;
  };

  validatePickupOrder = (pickupOrder: PickupSubOrderUpdate): string => {
    if (isNaN(pickupOrder.order_id)) {
      return 'Invalid order id provided.';
    }
    if (pickupOrder.shipping_id === '') {
      return 'Vrach ID moet worden ingevuld.';
    }
    if (pickupOrder.external_id === '') {
      return 'Ordernummer klant moet worden ingevuld.';
    }
    if (pickupOrder.customer_name === '') {
      return 'Klantnaam moet worden ingevuld.';
    }
    if (pickupOrder.shipping_city === '') {
      return 'Plaatsnaam moet worden ingevuld.';
    }
    if (pickupOrder.products.length === 0) {
      return 'Specify atleast 1 pickup order product';
    }

    return '';
  };

  render() {
    return html`
      <div class="wrapper">
        <div class="inner-wrapper">
          <div class="row centered">
            <h2>Order aanpassen</h2>
          </div>
          <div class="row action-row">
            <div class="label">Order ID:</div>
            <div class="prod-name">
              <input
                value=${this.pickupSubOrder.order_id}
                type="number"
                min="1"
                step="1"
                onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                id="orderID"
                name="orderID"
                @input=${() => {
                  const orderNumber = this.orderID
                    ? parseInt(this.orderID.value)
                    : NaN;
                  if (orderNumber.toString().length >= 6) {
                    this.salesOrderNumber = orderNumber.toString();
                  }
                }}
              />
            </div>
          </div>

          <div class="row action-row">
            <div class="label">Vracht ID:</div>
            <div class="prod-name">
              <input
                value=${this.pickupSubOrder.shipping_id}
                type="text"
                id="shippingID"
                name="shippingID"
              />
            </div>
          </div>

          <div class="row action-row">
            <div class="label">Ordernummer klant:</div>
            <div class="prod-name">
              <input
                value=${this.pickupSubOrder.external_id}
                type="text"
                id="externalID"
                name="externalID"
              />
            </div>
          </div>

          <div class="row action-row">
            <div class="label">Klantnaam:</div>
            <div class="prod-name">
              <input
                value=${this.pickupSubOrder.customer_name}
                type="text"
                id="customerName"
                name="customerName"
              />
            </div>
          </div>

          <div class="row action-row">
            <div class="label">Plaatsnaam:</div>
            <div class="prod-name">
              <input
                value=${this.pickupSubOrder.shipping_city}
                type="text"
                id="shippingCity"
                name="shippingCity"
              />
            </div>
          </div>
          <div class="row action-row">
            <div class="label">Pakbon notitie:</div>
            <div class="prod-name">
              <input
                value=${this.pickupSubOrder.package_receipt_note}
                type="text"
                id="packageReceiptNote"
                name="packageReceiptNote"
              />
            </div>
          </div>
          <div class="row action-row">
            <div class="label">PDF notitie:</div>
            <div class="prod-name">
              <textarea type="text" id="pdfNote" name="pdfNote">
${this.pickupSubOrder.pickup_order_pdf_comment}</textarea>
            </div>
          </div>
          <div class="row centered">
            <h2>Producten</h2>
          </div>
          <div class="row input-row">
            <div class="fill"></div>
            <div class="plus-icon" @click="${this.addProdInput}">
              ${plusIcon()}
            </div>
          </div>
          ${repeat(
            this.prods ? this.prods : [],
            (prod: ProdEdit) => prod.index,
            (item: ProdEdit, index: number) => {
              return html`
                <div class="row input-row prod-row">
                  <div class="input-column-prod-amount">
                    <input
                      value="${item.amount ? item.amount : ''}"
                      placeholder="aantal"
                      type="number"
                      min="1"
                      step="1"
                      class="product-amount"
                      name="product amount"
                      @input="${() => {
                        this.checkStockStatus();
                        this.calculateTotalWeight();
                      }}"
                    />
                  </div>
                  ${this.renderProductAmountUnits(index)}
                  <div class="input-column-prod">
                    <search-dropdown
                      .productCodeFilter="${this.productCodeFilter}"
                      .salesOrderNumber="${this.salesOrderNumber}"
                      .selected="${item.prod}"
                      class="search-dropdown"
                      @change="${(e: CustomEvent) => {
                        const event = JSON.parse(e.detail.value);
                        const temp = this.prods;
                        const curItem = temp[index];
                        if (curItem.prod) {
                          curItem.prod.id = event.id;
                          curItem.prod.description = event.description;
                          temp[index] = curItem;
                        }
                        this.prods = temp;
                        this.checkStockStatus();
                        this.calculateTotalWeight();
                      }}"
                    ></search-dropdown>
                  </div>
                  ${this.renderStockStatus(item.stock_status)}
                  <div
                    class="remove-prod"
                    @click="${() => {
                      this.removeProdInput(index);
                      setTimeout(() => {
                        this.checkStockStatus();
                        this.calculateTotalWeight();
                      }, 10);
                    }}"
                  >
                    ${minusIcon()}
                  </div>
                </div>
              `;
            },
          )}
          <div class="row input-row">
            <div class="label product-label">Totaal gewicht</div>
            <div class="fill"></div>
            <div>${this.totalWeight.toLocaleString('nl-NL')} kg</div>
          </div>
          ${this.renderPickupOrderPDF(this.pickupSubOrder)}
        </div>
      </div>
    `;
  }

  renderPickupOrderPDF = (subOrder: PickupSubOrder) => {
    if (!subOrder.pickup_order_pdf || subOrder.pickup_order_pdf === '') {
      return html``;
    }
    return html`
      <div class="row">
        <iframe
          class="pdf-viewer"
          src="data:application/pdf;base64,${subOrder.pickup_order_pdf}#navpanes=0"
        ></iframe>
      </div>
    `;
  };

  renderProductAmountUnits = (index: number) => {
    if (!this.shadowRoot) {
      return html` <div class="amount-in-units"></div> `;
    }
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    if (!prodAmountInputs[index]) {
      return html` <div class="amount-in-units"></div> `;
    }
    const amountInputElement = <HTMLInputElement>prodAmountInputs[index];
    if (!amountInputElement) {
      return html` <div class="amount-in-units"></div> `;
    }
    const amountValue = parseFloat(amountInputElement.value);

    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    if (!productSelects[index]) {
      return html` <div class="amount-in-units"></div> `;
    }
    const searchDropDown = <SearchDropdown>productSelects[index];
    if (!searchDropDown.selected) {
      return html` <div class="amount-in-units"></div> `;
    }
    return html`
      <div class="amount-in-units">
        ${
          Math.round(
            (amountValue / searchDropDown.selected.pieces_per_unit) * 1000,
          ) / 1000
        }
        ${searchDropDown.selected.unit}
      </div>
    `;
  };

  renderStockStatus = (stockStatus: number) => {
    if (stockStatus === StockStatusInStock) {
      return html`<div class="dot dot-green"></div>`;
    }
    if (stockStatus === StockStatusOutOfStock) {
      return html`<div class="dot dot-red"></div>`;
    }
    if (stockStatus === StockStatusLimited) {
      return html`<div class="dot dot-orange"></div>`;
    }
  };

  checkStockStatus = async () => {
    if (!this.shadowRoot) {
      return;
    }

    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    productSelects.forEach((e: Element, index: number) => {
      if (!this.pickupDate) {
        return;
      }

      const searchDropDown = <SearchDropdown>productSelects[index];
      const amountInputElement = <HTMLInputElement>prodAmountInputs[index];

      const amountValue = parseFloat(amountInputElement.value);
      const selected = searchDropDown.selected;

      if (
        !selected ||
        selected.id === undefined ||
        selected.id === '00000000-0000-0000-0000-000000000000'
      ) {
        return;
      }

      const pickupDate = new Date(this.pickupDate);
      if (selected && amountValue > 0) {
        this.checkStockStatusForIndex(index, pickupDate, amountValue, selected);
      }
    });
  };

  checkStockStatusForIndex = async (
    index: number,
    pickupDate: Date,
    amountValue: number,
    selected: Product,
  ) => {
    if (selected && amountValue > 0) {
      const productStockView = await this.getProductStock(selected.id);
      if (productStockView.stock_type == ProductStockTypeOnRequest) {
        return;
      }
      const toDeliver = amountValue / selected.pieces_per_unit;
      let stockAmountAtPickup = productStockView.stock_amount;
      if (productStockView.delivery_over_time) {
        productStockView.delivery_over_time.forEach(
          (delivery: DeliveryOverTime) => {
            const deliveryDate = new Date(delivery.date);
            if (deliveryDate <= pickupDate) {
              stockAmountAtPickup -= delivery.amount_in_units;
            }
          },
        );
      }
      if (productStockView.planned_production_over_time) {
        productStockView.planned_production_over_time.forEach(
          (delivery: DeliveryOverTime) => {
            const deliveryDate = new Date(delivery.date);
            if (deliveryDate <= pickupDate) {
              stockAmountAtPickup += delivery.amount;
            }
          },
        );
      }
      const valueWithNewPickup = stockAmountAtPickup - toDeliver;
      let stockStatus = StockStatusInStock;
      if (productStockView.not_in_stock_amount > valueWithNewPickup) {
        stockStatus = StockStatusOutOfStock;
      } else if (productStockView.limited_stock_amount > valueWithNewPickup) {
        stockStatus = StockStatusLimited;
      }
      const temp = this.prods;
      temp[index].stock_status = stockStatus;
      this.prods = [];
      this.prods = temp;
    }
  };

  getProducts = (): Product[] => {
    if (!this.shadowRoot) {
      return [];
    }
    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    const result: Product[] = [];
    for (let i = 0; i < productSelects.length; i++) {
      const e = <SearchDropdown>productSelects[i];
      const searchDropDown = e;
      const amountInputElement = <HTMLInputElement>prodAmountInputs[i];
      if (!searchDropDown.selected) {
        showNotification(`Product nummer ${i + 1} is niet geselecteerd.`);
        return [];
      }
      if (
        !amountInputElement.value ||
        parseFloat(amountInputElement.value) == 0
      ) {
        showNotification(`Product nummer ${i + 1} heeft geen aantal ingevuld.`);
        return [];
      }
      const prod = searchDropDown.selected;
      prod.product_id = prod.id;
      prod.amount = parseFloat(amountInputElement.value);
      result.push(prod);
    }
    return result;
  };

  addProdInput = () => {
    const temp = this.prods;
    this.prodIndex++;
    temp.push({
      amount: 0.0,
      index: this.prodIndex,
      stock_status: StockStatusInStock,
    });
    this.prods = [];
    this.prods = temp;
  };

  removeProdInput = (index: number) => {
    if (!this.shadowRoot) {
      return;
    }
    if (this.prods.length <= 1) {
      showNotification('A pickup order needs atleast 1 product.');
      return;
    }
    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    const newProds: ProdEdit[] = [];
    prodAmountInputs.forEach((e: Element, otherIndex: number) => {
      if (otherIndex !== index) {
        const inputElement = <HTMLInputElement>e;
        const searchDropDown = <SearchDropdown>productSelects[otherIndex];

        newProds.push({
          amount: parseFloat(inputElement.value),
          index: this.prods[otherIndex].index,
          prod: searchDropDown.selected,
          stock_status: StockStatusInStock,
        });
      }
    });
    this.prods = [];
    this.prods = newProds;
  };

  calculateTotalWeight = () => {
    setTimeout(() => {
      if (!this.shadowRoot) {
        return;
      }
      const productSelects =
        this.shadowRoot.querySelectorAll('.search-dropdown');
      const prodAmountInputs =
        this.shadowRoot.querySelectorAll('.product-amount');
      const result: ProdEdit[] = [];
      productSelects.forEach((e: Element, index: number) => {
        const searchDropDown = <SearchDropdown>e;
        const amountInputElement = <HTMLInputElement>prodAmountInputs[index];
        result.push(<ProdEdit>{
          amount: parseFloat(amountInputElement.value),
          prod: searchDropDown.selected ? searchDropDown.selected : undefined,
        });
      });
      let totalWeight = 0;
      result.forEach((p: ProdEdit) => {
        if (!p.prod || isNaN(p.amount)) {
          return;
        }
        totalWeight += p.amount * p.prod.weight;
      });
      totalWeight = Math.round(totalWeight);

      this.totalWeight = totalWeight;
    }, 5);
  };

  searchOrderLinesExact = async (orderNumber: number) => {
    if (this.exactCalled) {
      return;
    }
    const path = `/exact-globe/sales-order-lines/${orderNumber}`;
    const result = await authenticatedAPICall(path, 'GET', new Headers());

    if (isSuccessfulAPIResponse(result)) {
      this.exactCalled = true;
      const codes = [];
      for (let i = 0; i < result.body.length; i++) {
        codes.push(result.body[i].item_code);
      }
      const res: ProductExact[] = [];
      for (let i = 0; i < result.body.length; i++) {
        res.push(result.body[i]);
      }
      this.exactProducts = res;
      const resultFilter = codes.join(',');
      this.productCodeFilter = resultFilter;
    }
  };

  getProductStock = async (id: string): Promise<ProductStockView> => {
    const result = await authenticatedAPICall(
      `/stock/products/${id}`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      return result.body;
    }
    return <ProductStockView>{};
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-update-pickup-sub-order': UpdatePickupSubOrder;
  }
}
